import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  TrendingUp,
  Users,
  CircleDollarSign,
  BookOpen,
  Loader2,
  AlertTriangle,
  Inbox,
  CheckCircle2,
  Eye,
  Calendar,
  Info,
  Wallet,
} from 'lucide-react';
import {
  useGetInstructorAnalyticsQuery,
  useGetInstructorOrdersQuery,
} from '../../api/apiSlice';
import Pagination from '../../components/common/Pagination';

// InstructorAnalyticsPage
// ──────────────────────
// Дэшборд инструктора: KPI, доход за период, перформанс курсов,
// последние покупки. Один эндпоинт `/api/instructor/analytics` отдаёт всё.
//
// Все сложные агрегации делает бэк ($facet / aggregate). Фронт только
// рендерит. Поэтому страница быстрая даже на 50+ курсах.

const fmtKzt = (n) => `${Number(n || 0).toLocaleString('ru-RU')} ₸`;
const fmtNum = (n) => Number(n || 0).toLocaleString('ru-RU');
const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'short',
      })
    : '—';
const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

// Календарь + quick-presets. Бэк принимает ?from=YYYY-MM-DD&to=YYYY-MM-DD.
const fmtIso = (d) => {
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return fmtIso(d);
};
const today = () => fmtIso(new Date());

// labelKey резолвится через t() при рендере → переключение языка работает.
const PRESETS = [
  { id: '7',   labelKey: 'instructorAnalytics.preset7',   getRange: () => ({ from: daysAgo(7),   to: today() }) },
  { id: '30',  labelKey: 'instructorAnalytics.preset30',  getRange: () => ({ from: daysAgo(30),  to: today() }) },
  { id: '90',  labelKey: 'instructorAnalytics.preset90',  getRange: () => ({ from: daysAgo(90),  to: today() }) },
  { id: '365', labelKey: 'instructorAnalytics.preset365', getRange: () => ({ from: daysAgo(365), to: today() }) },
];

// Sparkline через инлайновый SVG. Без библиотек — для маленького чарта
// recharts/chart.js это overkill. SVG path точно встанет в дашборд по теме.
const Sparkline = ({ data, height = 60 }) => {
  const max = Math.max(1, ...data.map((d) => d.revenue));
  const width = 100; // percentage-based viewBox
  const points = data
    .map((d, i) => {
      const x = (i / Math.max(1, data.length - 1)) * width;
      const y = height - (d.revenue / max) * height;
      return `${x},${y}`;
    })
    .join(' ');
  const area = `M0,${height} L${points} L${width},${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-16"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(99,102,241)" stopOpacity="0.4" />
          <stop offset="100%" stopColor="rgb(99,102,241)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sparkGrad)" />
      <polyline
        fill="none"
        stroke="rgb(99,102,241)"
        strokeWidth="1.5"
        points={points}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const ORDERS_PAGE_SIZE = 20;

const InstructorAnalyticsPage = () => {
  const { t } = useTranslation();
  // Дефолт — последние 30 дней. Юзер меняет через preset-кнопки или календарь.
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(today());
  const [ordersPage, setOrdersPage] = useState(1);
  const { data, isLoading, isFetching, error } = useGetInstructorAnalyticsQuery({
    from: dateFrom,
    to: dateTo,
  });

  // Какой preset активен сейчас (даты совпадают с его диапазоном)?
  const activePreset = (() => {
    for (const p of PRESETS) {
      const r = p.getRange();
      if (r.from === dateFrom && r.to === dateTo) return p.id;
    }
    return null;
  })();
  const applyPreset = (p) => {
    const r = p.getRange();
    setDateFrom(r.from);
    setDateTo(r.to);
  };

  // Полный пагинированный список покупок — отдельный запрос. Без status param
  // бэк отдаёт оба типа успешных заказов (paid legacy KaspiPay + confirmed P2P).
  const { data: ordersData, isFetching: ordersFetching } = useGetInstructorOrdersQuery({
    page: ordersPage,
    limit: ORDERS_PAGE_SIZE,
  });

  const d = data || {};
  const summary = d.summary || {};
  const performance = d.coursePerformance || [];
  const timeline = d.revenueTimeline || [];
  // recentEnrollments больше не используем — теперь /analytics/orders с пагинацией.

  // Максимум revenue по timeline для подписей.
  const maxRevenueInPeriod = useMemo(
    () => Math.max(0, ...timeline.map((r) => r.revenue)),
    [timeline]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:bg-red-950/20 p-5 text-red-700 dark:text-red-300 max-w-xl">
        <AlertTriangle className="w-5 h-5 inline mr-1" />
        {error?.data?.message || t('instructorAnalytics.loadError')}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('instructorAnalytics.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('instructorAnalytics.subtitle')}
          </p>
        </div>

        {/* Period selector — presets + календарный диапазон. */}
        <div className="space-y-2">
          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p)}
                disabled={isFetching}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                  activePreset === p.id
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {t(p.labelKey)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || undefined}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title={t('instructorAnalytics.dateFrom')}
            />
            <span className="text-gray-400">—</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              max={today()}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title={t('instructorAnalytics.dateTo')}
            />
          </div>
        </div>
      </div>

      {/* Пояснение разницы Analytics vs Finance — частый вопрос инструктора. */}
      <div className="rounded-2xl border border-purple-200/70 dark:border-purple-900/40 bg-purple-50/50 dark:bg-purple-950/20 p-4 flex items-start gap-3">
        <Info className="h-4 w-4 mt-0.5 text-purple-600 dark:text-purple-400 shrink-0" />
        <div className="text-sm text-gray-700 dark:text-gray-300 flex-1">
          <p>
            <strong className="text-gray-900 dark:text-white">{t('instructorAnalytics.infoP1Strong')}</strong>{t('instructorAnalytics.infoP1Text')}
          </p>
          <p className="mt-1">
            {t('instructorAnalytics.infoP2Prefix')} <strong>{t('instructorAnalytics.infoP2Bold')}</strong> {t('instructorAnalytics.infoP2Mid')} <Link to="/dashboard/instructor/finance" className="text-purple-700 dark:text-purple-300 font-semibold inline-flex items-center gap-1 hover:underline">
              <Wallet className="h-3.5 w-3.5" /> {t('instructorAnalytics.infoFinanceLink')}
            </Link>.
          </p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          icon={CircleDollarSign}
          label={t('instructorAnalytics.kpiPeriodRevenue')}
          value={fmtKzt(summary.periodRevenue)}
          sub={t('instructorAnalytics.kpiPeriodRevenueSub', { count: fmtNum(summary.periodOrders) })}
          color="emerald"
        />
        <Kpi
          icon={TrendingUp}
          label={t('instructorAnalytics.kpiTotalRevenue')}
          value={fmtKzt(summary.totalRevenue)}
          sub={t('instructorAnalytics.kpiTotalRevenueSub')}
          color="indigo"
        />
        <Kpi
          icon={Users}
          label={t('instructorAnalytics.kpiStudents')}
          value={fmtNum(summary.totalStudents)}
          color="blue"
        />
        <Kpi
          icon={BookOpen}
          label={t('instructorAnalytics.kpiCourses')}
          value={fmtNum(summary.totalCourses)}
          sub={t('instructorAnalytics.kpiCoursesSub', { count: fmtNum(summary.activeCourses) })}
          color="purple"
        />
      </div>

      {/* Revenue timeline */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">
            {t('instructorAnalytics.revenueDynamics')}
          </h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('instructorAnalytics.maxPerDay', { amount: fmtKzt(maxRevenueInPeriod) })}
          </span>
        </div>
        {timeline.length > 0 && maxRevenueInPeriod > 0 ? (
          <>
            <Sparkline data={timeline} />
            <div className="mt-2 flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400">
              <span>{fmtDate(timeline[0]?.date)}</span>
              <span>{fmtDate(timeline[timeline.length - 1]?.date)}</span>
            </div>
          </>
        ) : (
          <Empty icon={Calendar} text={t('instructorAnalytics.emptyTimeline')} />
        )}
      </section>

      {/* Revenue distribution — круговой donut chart с долей каждого курса.
          Числа за выбранный период (синхронно с календарём). */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">
            {t('instructorAnalytics.donutTitle')}
          </h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('instructorAnalytics.donutPeriod')}
          </span>
        </div>
        <RevenueDonut performance={performance} periodRevenue={summary.periodRevenue} t={t} />
      </section>

      {/* Course performance */}
      <section>
        <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">
          {t('instructorAnalytics.performance')}
        </h2>
        {performance.length === 0 ? (
          <Empty icon={Inbox} text={t('instructorAnalytics.performanceEmpty')} />
        ) : (
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">{t('instructorAnalytics.thCourse')}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t('instructorAnalytics.thStudents')}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t('instructorAnalytics.thCompleted')}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t('instructorAnalytics.thRevenue')}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t('instructorAnalytics.thActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {performance.map((c) => (
                    <tr
                      key={c._id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/40"
                    >
                      <td className="px-4 py-3 max-w-[280px]">
                        {/* R2 cost-save: НЕ грузим coverImageUrl в аналитической
                            таблице. Каждое открытие страницы = N R2-reads.
                            Заменяем на буквенный thumbnail из названия курса. */}
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 text-sm font-semibold shrink-0">
                            {(c.title || '?').trim().charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 dark:text-white truncate">
                              {c.title}
                            </div>
                            <StatusBadge status={c.status} />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {fmtNum(c.students)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <div className="text-emerald-600 dark:text-emerald-400 font-semibold">
                          {c.completionRate}%
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {t('instructorAnalytics.avgProgress', { n: c.avgProgress })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900 dark:text-white">
                        {fmtKzt(c.revenue)}
                        <div className="text-[10px] text-gray-500 font-normal">
                          {t('instructorAnalytics.ordersCount', { count: fmtNum(c.orders) })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/dashboard/instructor/create-course/${c._id}/edit`}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 px-2.5 py-1 text-xs font-medium"
                        >
                          <Eye className="w-3 h-3" /> {t('instructorAnalytics.openCourse')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Orders — пагинированный список. Раньше тут было хардкоженных
          10 из recentEnrollments — теперь полная история через
          /instructor/analytics/orders с пагинацией. */}
      <section>
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">
              {t('instructorAnalytics.ordersTitle')}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {t('instructorAnalytics.ordersSubtitle')}
              {ordersData?.total !== undefined && ` ${t('instructorAnalytics.ordersTotal', { count: fmtNum(ordersData.total) })}`}
            </p>
          </div>
        </div>

        {!ordersData?.items?.length && !ordersFetching ? (
          <Empty icon={Users} text={t('instructorAnalytics.ordersEmpty')} />
        ) : (
          <>
            <div className={`rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800 transition-opacity ${ordersFetching ? 'opacity-60' : ''}`}>
              {(ordersData?.items || []).map((r) => (
                <div key={r._id} className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold shrink-0">
                    {(r.user?.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {r.user?.name || '—'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('instructorAnalytics.boughtVerb', { title: r.course?.title || '—' })}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                      {fmtKzt(r.amount)}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                      {fmtDateTime(r.paidAt || r.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {ordersData && ordersData.pageCount > 1 && (
              <div className="mt-5">
                <Pagination
                  page={ordersData.page}
                  pageCount={ordersData.pageCount}
                  onChange={(p) => setOrdersPage(p)}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

// ─── pieces ─────────────────────────────────────────────
const KPI_PALETTE = {
  indigo: 'bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
  blue: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300',
  purple: 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300',
  emerald: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
};

const Kpi = ({ icon: Icon, label, value, sub, color = 'indigo' }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <span className={`p-1.5 rounded-lg ${KPI_PALETTE[color]}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
    </div>
    <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tabular-nums truncate">
      {value}
    </div>
    {sub && (
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
        {sub}
      </div>
    )}
  </div>
);

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const map = {
    draft: { key: 'statusDraft', cls: 'text-gray-500' },
    pending_review: { key: 'statusPendingReview', cls: 'text-amber-600' },
    active: { key: 'statusActive', cls: 'text-emerald-600' },
    rejected: { key: 'statusRejected', cls: 'text-red-600' },
    archived: { key: 'statusArchived', cls: 'text-gray-500' },
  };
  const s = map[status] || map.draft;
  return <div className={`text-[10px] ${s.cls}`}>{t(`instructorAnalytics.${s.key}`)}</div>;
};

const Empty = ({ icon: Icon, text }) => (
  <div className="rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-10 text-center text-gray-500 dark:text-gray-400">
    <Icon className="w-10 h-10 mx-auto mb-2 text-gray-400" />
    <p className="text-sm">{text}</p>
  </div>
);

// Палитра для donut-сегментов и легенды. Топ-3 — яркие фирменные цвета,
// дальше — нейтральные. Hex-значения (не Tailwind), чтобы напрямую вставить в SVG.
const DONUT_COLORS = [
  '#6366f1', // indigo-500 — #1, бестселлер
  '#22c55e', // green-500  — #2
  '#3b82f6', // blue-500   — #3
  '#f59e0b', // amber-500  — #4
  '#ec4899', // pink-500   — #5
  '#14b8a6', // teal-500   — #6
  '#8b5cf6', // violet-500 — #7
  '#94a3b8', // slate-400  — #8+
];
const colorFor = (idx) => DONUT_COLORS[Math.min(idx, DONUT_COLORS.length - 1)];

// Круговой donut chart с центральной суммой + легенда справа (на ld+).
// SVG: один полный «фоновый» круг + N арок поверх, каждая арка = доля курса.
// Используем stroke-dasharray + stroke-dashoffset для рисования сегментов.
// rotate(-90) на круге — старт от 12 часов (более интуитивно чем от 3 часов).
const RevenueDonut = ({ performance, periodRevenue, t }) => {
  // Курсы без дохода в этом периоде скрываем — они только мусор в чарте.
  const slices = performance.filter((c) => c.revenue > 0);

  if (slices.length === 0) {
    return <Empty icon={CircleDollarSign} text={t('instructorAnalytics.donutEmpty')} />;
  }

  // Геометрия. radius=70, strokeWidth=22 → внутренний радиус 48 → центр для текста.
  // viewBox -100..100 → ширина 200 (центр в 0,0). Финальный размер задаём через CSS.
  const R = 70;
  const SW = 22;
  const C = 2 * Math.PI * R;
  // Минимальный размер сегмента — 0.6% — чтобы крошки курса всё равно
  // были визуально различимы и кликабельны на легенде.
  const MIN_SHARE = 0.6;

  let accumulated = 0;
  const arcs = slices.map((s, idx) => {
    const share = Math.max(MIN_SHARE, s.revenueShare);
    const length = (share / 100) * C;
    const offset = -accumulated;
    accumulated += length;
    return { id: s._id, color: colorFor(idx), length, offset };
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 items-center">
      {/* Donut */}
      <div className="relative mx-auto" style={{ width: 220, height: 220 }}>
        <svg viewBox="-100 -100 200 200" className="w-full h-full -rotate-90">
          {/* Фоновое кольцо — заполняет 100% слабым цветом. */}
          <circle
            r={R}
            fill="none"
            stroke="currentColor"
            className="text-gray-100 dark:text-gray-800"
            strokeWidth={SW}
          />
          {/* Сегменты курсов. */}
          {arcs.map((a) => (
            <circle
              key={a.id}
              r={R}
              fill="none"
              stroke={a.color}
              strokeWidth={SW}
              strokeLinecap="butt"
              strokeDasharray={`${a.length} ${C}`}
              strokeDashoffset={a.offset}
              style={{ transition: 'stroke-dasharray 500ms, stroke-dashoffset 500ms' }}
            />
          ))}
        </svg>
        {/* Центральный лейбл — общая сумма за период. */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('instructorAnalytics.donutCenterLabel')}
          </div>
          <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
            {fmtKzt(periodRevenue)}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400">
            {t('instructorAnalytics.donutCount', { count: slices.length })}
          </div>
        </div>
      </div>

      {/* Легенда — компактный список со swatch'ами слева. */}
      <ul className="space-y-2.5">
        {slices.map((c, idx) => (
          <li
            key={c._id}
            className="flex items-center gap-3 group hover:bg-gray-50 dark:hover:bg-gray-800/50 px-2 py-1.5 -mx-2 rounded-lg transition"
          >
            <span
              className="w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor: colorFor(idx) }}
            />
            <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tabular-nums shrink-0 w-5">
              #{idx + 1}
            </span>
            <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-white truncate">
              {c.title}
            </span>
            <span className="shrink-0 text-right">
              <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                {fmtKzt(c.revenue)}
              </span>
              <span
                className="ml-1.5 text-xs font-bold tabular-nums"
                style={{ color: colorFor(idx) }}
              >
                {c.revenueShare}%
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default InstructorAnalyticsPage;
